import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Paper,
  TextField,
  Button,
  MenuItem,
  Select,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "./AddNewService.css";

const todayDate = new Date().toISOString().split("T")[0];

const defaultRow = {
  id: 1,
  userName: "",
  openDate: todayDate,
  projectName: "",
  customerName: "",
  location: "",
  serviceMode: "",
  modelNumber: "",
  issue: "",
  warranty: "",
  serviceType: "",
  specifications: "",
  closeDate: "",
  comment: "",
};

const AddNewService = ({ onClose }) => {
  const { id: userId } = useParams();
  const [projectDetails, setProjectDetails] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [row, setRow] = useState(defaultRow);
  const [isCommentSectionOpen, setIsCommentSectionOpen] = useState(false);
  const [comment, setComment] = useState("");
  const suggestionsRef = useRef(null);

  // ✅ Fetch User Name
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("jwt");
        if (!token) {
          console.warn("Token not found in localStorage");
          // Optionally, redirect user or show a message
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}user-details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch user details");

        const user = await response.json();
        console.log("Fetched user:", user);

        if (user?.FirstName || user?.LastName) {
          const fullName = `${user.FirstName || ""} ${user.LastName || ""}`.trim();
          setRow((prev) => ({ ...prev, userName: fullName }));
        }
      } catch (err) {
        console.error("Error fetching user details:", err);
      }
    };

    fetchUserDetails();
  }, []);


  const handleInputChange = (field, value) => {
    const updatedRow = { ...row, [field]: value };

    if (field === "projectName") {
      const filtered = projectDetails.filter((p) =>
        p.ProjectName?.toLowerCase().startsWith(value.toLowerCase().trim())
      );
      setSuggestions(value ? filtered : []);
      if (value === "") {
        updatedRow.location = "";
      }
    }

    setRow(updatedRow);
  };

  const handleServiceModeChange = (value) => {
    setRow((prev) => ({ ...prev, serviceMode: value, specifications: "" }));
  };

  const handleSpecificationsChange = (value) => {
    setRow((prev) => ({ ...prev, specifications: value }));
  };

  const handleSuggestionClick = (suggestion) => {
    setRow((prev) => ({
      ...prev,
      projectName: suggestion.ProjectName,
      location: suggestion.location,
    }));
    setSuggestions([]);
  };

  const handleClickOutside = (e) => {
    if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCommentClick = () => {
    setComment(row.comment || "");
    setIsCommentSectionOpen(true);
  };

  const handleSaveComment = () => {
    setRow((prev) => ({ ...prev, comment }));
    setIsCommentSectionOpen(false);
  };

  const handleCancelComment = () => {
    setIsCommentSectionOpen(false);
  };

  const handleSubmit = async () => {
    const payload = {
      ...row,
      openDate: row.openDate || todayDate,
    };

    if (row.closeDate && !row.closeComment.trim()) {
      toast.warning("Close Comment is required when Close Date is selected");
      return;
    }


    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}add-service/${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rows: [payload] }),
        }
      );

      if (!response.ok) throw new Error("Failed to submit");

      toast.success("Service added successfully!");
      setRow({ ...defaultRow, userName: row.userName }); // Keep userName
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to submit service details.");
    }
  };

  return (
    <div className="projecttracker-wrap">
      <Paper className="add-project">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="User Name"
              value={row.userName}
              disabled
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              type="date"
              label="Open Date"
              value={row.openDate}
              onChange={(e) => handleInputChange("openDate", e.target.value)}
              fullWidth
              InputProps={{
                inputProps: { max: todayDate },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Project Name"
              value={row.projectName}
              onChange={(e) => handleInputChange("projectName", e.target.value)}
              fullWidth
            />
            {suggestions.length > 0 && (
              <div ref={suggestionsRef} className="suggestions-dropdown">
                {suggestions.map((s, i) => (
                  <MenuItem key={i} onClick={() => handleSuggestionClick(s)}>
                    {s.ProjectName}
                  </MenuItem>
                ))}
              </div>
            )}
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Customer Name"
              value={row.customerName}
              onChange={(e) => handleInputChange("customerName", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Location"
              value={row.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <Select
              value={row.serviceMode || ""}
              onChange={(e) => handleServiceModeChange(e.target.value)}
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>Select Mode</MenuItem>
              <MenuItem value="Technical Call">Technical Call</MenuItem>
              <MenuItem value="Site Visit">Site Visit</MenuItem>
              <MenuItem value="In House">In House</MenuItem>
            </Select>

            {row.serviceMode && (
              <TextField
                label="Add Specifications"
                value={row.specifications}
                onChange={(e) => handleSpecificationsChange(e.target.value)}
                placeholder="Enter specifications, separated by commas"
                fullWidth
                multiline
              />
            )}
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Model Number"
              value={row.modelNumber}
              onChange={(e) => handleInputChange("modelNumber", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Issue"
              value={row.issue}
              onChange={(e) => handleInputChange("issue", e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <Select
              value={row.warranty || ""}
              onChange={(e) => handleInputChange("warranty", e.target.value)}
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>Select Warranty</MenuItem>
              <MenuItem value="In warranty">In Warranty</MenuItem>
              <MenuItem value="Expired">Expired</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6}>
            <Select
              value={row.serviceType || ""}
              onChange={(e) => handleInputChange("serviceType", e.target.value)}
              fullWidth
              displayEmpty
            >
              <MenuItem value="" disabled>Select Type</MenuItem>
              <MenuItem value="Paid Service">Paid Service</MenuItem>
              <MenuItem value="Free Service">Free Service</MenuItem>
              <MenuItem value="To Be Discussed">To Be Discussed</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Close Date"
              type="date"
              value={row.closeDate}
              onChange={(e) => handleInputChange("closeDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {row.closeDate && (
            <Grid item xs={6}>
              <TextField
                label="Close Comment"
                value={row.closeComment}
                onChange={(e) =>
                  handleInputChange("closeComment", e.target.value)
                }
                fullWidth
                multiline
                rows={3}
             
              />
            </Grid>
          )}


          {/* Comment Section */}
          <Grid item xs={6}>
            {row.comment ? (
              <div className="comment-preview-box">
                <p className="comment-preview">{row.comment}</p>
                <Button className="edit-btn" onClick={handleCommentClick}>
                  Edit
                </Button>
              </div>
            ) : (
              <Button className="comment-btn" onClick={handleCommentClick} fullWidth>
                Add Comment
              </Button>
            )}

            {isCommentSectionOpen && (
              <div className="comment-overlay">
                <div className="comment-box p-2">
                  <textarea
                    className="comment-input border-1 border-gray-400"
                    rows={5}
                    value={comment}
                    
                    onChange={(e) => setComment(e.target.value)}
                  />
             

                  <div className="button-box flex gap-2 justify-between">
                    <Button className="submit-btn" onClick={handleSaveComment}>
                      Save
                    </Button>
                    <Button className="submit-btn" onClick={handleCancelComment}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Grid>

          <Grid item xs={6}>
            <Button className="submit-project-btn" onClick={handleSubmit} fullWidth>
              Submit
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button className="cancle-project-btn" onClick={onClose} fullWidth>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <ToastContainer />
    </div>
  );
};

export default AddNewService;
